import { LocalStorageKeys } from "constants/index";
import dayjs, { Dayjs } from "dayjs";
import {
  FactSetOption,
  Strategy,
  StrategyPosition,
} from "features/trading/accounts";
import { TradeMode } from "features/trading/tradingSlice";
import { ColumnPeriodT, ReportTabT } from "./types";

export const getTradeModeLabel = (mode: TradeMode) => {
  switch (mode) {
    case TradeMode.CallSellShort:
      return "Sell Short";
    case TradeMode.CallBuyToCover:
      return "Buy to Cover";
    case TradeMode.CallRebalance:
      return "Rebalance";
    case TradeMode.MultiLeg:
      return "MLO";
    case TradeMode.PutBuyLong:
      return "Buy Long";
    case TradeMode.PutSellCover:
      return "Sell to Cover";
    default:
      return "Unknown";
  }
};

export const asMillionsCurrency = (value: number) => {
  const millionsValue = value / 1_000_000;

  const currencyFormatted = Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 3,
  }).format(millionsValue);

  return `${currencyFormatted}M`;
};

export const getOnOrder = (position: StrategyPosition) => {
  const { on_order_sell, on_order_buy } = position;
  const onOrderSell = -parseInt(on_order_sell || "0");
  const onOrderBuy = parseInt(on_order_buy || "0");
  return onOrderSell + onOrderBuy;
};

export const getExecutedOrder = (position: StrategyPosition) => {
  const { executed_order_sell, executed_order_buy } = position;
  const executedOrderSell = -parseInt(executed_order_sell || "0");
  const executedOrderBuy = parseInt(executed_order_buy || "0");
  return executedOrderSell + executedOrderBuy;
};

export const isCallExpired = (strategy: Strategy) => {
  const { current_call__name } = strategy;
  if (current_call__name === null) {
    return true;
  }
  const date = current_call__name.split(" ")[2];
  const daysDiff = dayjs(date).diff(dayjs(), "day");
  return daysDiff < 0;
};

export const mapOptionSymbol = (
  stock: string,
  expDate: Dayjs,
  strikePrice: number,
  call_or_put: boolean,
) => {
  const underlyingPart = stock.padEnd(6, " ");
  const expDatePart = expDate.format("YYMMDD");
  const strikePricePart = Number(strikePrice.toFixed(2))
    .toFixed(3)
    .replace(".", "")
    .padStart(8, "0");

  if (call_or_put) {
    return underlyingPart + expDatePart + "P" + strikePricePart;
  } else {
    return underlyingPart + expDatePart + "C" + strikePricePart;
  }
};

export const mapOptionName = (
  stock: string,
  expDate: Dayjs,
  strikePrice: string,
  optionType: string = "C",
) => {
  return `${optionType === "C" ? "Call" : "Put"} ${stock} ${expDate.format("M-D-YY")} @ ${Number(strikePrice).toFixed(2).replace(".00", "")}`;
};

export const updateLastStrategyGroupId = (id?: number) => {
  if (id) {
    localStorage.setItem(LocalStorageKeys.LAST_STRATEGY_GROUP_ID, String(id));
  } else {
    localStorage.removeItem(LocalStorageKeys.LAST_STRATEGY_GROUP_ID);
  }
};

export const updateLastStrategy = (strategy: Strategy) => {
  if (strategy) {
    localStorage.setItem(
      LocalStorageKeys.LAST_STRATEGY_ID,
      String(strategy.id),
    );
    localStorage.setItem(
      LocalStorageKeys.LAST_STRATEGY_GROUP_ID,
      String(strategy.strategy_group),
    );
  } else {
    localStorage.removeItem(LocalStorageKeys.LAST_STRATEGY_ID);
    localStorage.removeItem(LocalStorageKeys.LAST_STRATEGY_GROUP_ID);
  }
};

export const updateLastStatisticStrategyId = (id?: number) => {
  if (id) {
    localStorage.setItem(
      LocalStorageKeys.LAST_STATISTIC_STRATEGY_ID,
      String(id),
    );
  } else {
    localStorage.removeItem(LocalStorageKeys.LAST_STATISTIC_STRATEGY_ID);
  }
};

export const updateLastStatisticAccountId = (id?: number) => {
  if (id) {
    localStorage.setItem(
      LocalStorageKeys.LAST_STATISTIC_ACCOUNT_ID,
      String(id),
    );
  } else {
    localStorage.removeItem(LocalStorageKeys.LAST_STATISTIC_ACCOUNT_ID);
  }
};

export const updateLastStatisticDateRange = (
  range: [string | null, string | null],
) => {
  if (range) {
    localStorage.setItem(
      LocalStorageKeys.LAST_STATISTIC_DATE_RANGE,
      JSON.stringify(range),
    );
  } else {
    localStorage.removeItem(LocalStorageKeys.LAST_STATISTIC_DATE_RANGE);
  }
};

export const updateLastStatisticPeriods = (periods: ColumnPeriodT[]) => {
  if (periods) {
    localStorage.setItem(
      LocalStorageKeys.LAST_STATISTIC_PERIODS,
      JSON.stringify(periods),
    );
  } else {
    localStorage.removeItem(LocalStorageKeys.LAST_STATISTIC_PERIODS);
  }
};

export const updateLastPerformanceStockClientId = (id?: number) => {
  if (id) {
    localStorage.setItem(LocalStorageKeys.LAST_CLIENT_STOCK_ID, String(id));
  } else {
    localStorage.removeItem(LocalStorageKeys.LAST_CLIENT_STOCK_ID);
  }
};

export const updateLastPerformanceStockAccountIds = (ids?: number[]) => {
  if (ids) {
    localStorage.setItem(
      LocalStorageKeys.LAST_ACCOUNT_STOCK_IDS,
      JSON.stringify(ids),
    );
  } else {
    localStorage.removeItem(LocalStorageKeys.LAST_ACCOUNT_STOCK_IDS);
  }
};

export const updateLastPerformanceStockPeriods = (periods: ColumnPeriodT[]) => {
  if (periods) {
    localStorage.setItem(
      LocalStorageKeys.LAST_PERIODS_STOCK,
      JSON.stringify(periods),
    );
  } else {
    localStorage.removeItem(LocalStorageKeys.LAST_PERIODS_STOCK);
  }
};

export const updateLastPerformanceStrategyClientId = (id?: number) => {
  if (id) {
    localStorage.setItem(LocalStorageKeys.LAST_CLIENT_STRATEGY_ID, String(id));
  } else {
    localStorage.removeItem(LocalStorageKeys.LAST_CLIENT_STRATEGY_ID);
  }
};

export const updateLastPerformanceStrategyAccountIds = (ids?: number[]) => {
  if (ids) {
    localStorage.setItem(
      LocalStorageKeys.LAST_ACCOUNT_STRATEGY_IDS,
      JSON.stringify(ids),
    );
  } else {
    localStorage.removeItem(LocalStorageKeys.LAST_ACCOUNT_STRATEGY_IDS);
  }
};

export const updateLastPerformanceStrategyPeriods = (
  periods: ColumnPeriodT[],
) => {
  if (periods) {
    localStorage.setItem(
      LocalStorageKeys.LAST_PERIODS_STRATEGY,
      JSON.stringify(periods),
    );
  } else {
    localStorage.removeItem(LocalStorageKeys.LAST_PERIODS_STRATEGY);
  }
};

export const updateLastReportTab = (tab: ReportTabT) => {
  localStorage.setItem(LocalStorageKeys.LAST_REPORT_TAB, tab);
};

export const getLastStrategyId = () =>
  Number(localStorage.getItem(LocalStorageKeys.LAST_STRATEGY_ID) || "0");
export const getLastStrategyGroupId = () =>
  Number(localStorage.getItem(LocalStorageKeys.LAST_STRATEGY_GROUP_ID) || "0");
export const getLastStatisticStrategyId = () =>
  Number(
    localStorage.getItem(LocalStorageKeys.LAST_STATISTIC_STRATEGY_ID) || "0",
  );
export const getLastStatisticAccountId = () =>
  Number(
    localStorage.getItem(LocalStorageKeys.LAST_STATISTIC_ACCOUNT_ID) || "0",
  );
export const getLastStatisticDateRange = (): [string | null, string | null] =>
  JSON.parse(
    localStorage.getItem(LocalStorageKeys.LAST_STATISTIC_DATE_RANGE) || "[]",
  );
export const getLastStatisticPeriods = (): ColumnPeriodT[] =>
  JSON.parse(
    localStorage.getItem(LocalStorageKeys.LAST_STATISTIC_PERIODS) || "[]",
  );

export const getLastPerformanceStockClientId = () =>
  Number(localStorage.getItem(LocalStorageKeys.LAST_CLIENT_STOCK_ID) || "0");
export const getLastPerformanceStockAccountIds = (): number[] =>
  JSON.parse(
    localStorage.getItem(LocalStorageKeys.LAST_ACCOUNT_STOCK_IDS) || "[]",
  );
export const getLastPerformanceStockPeriods = (): ColumnPeriodT[] =>
  JSON.parse(localStorage.getItem(LocalStorageKeys.LAST_PERIODS_STOCK) || "[]");
export const getLastPerformanceStrategyClientId = () =>
  Number(localStorage.getItem(LocalStorageKeys.LAST_CLIENT_STRATEGY_ID) || "0");
export const getLastPerformanceStrategyAccountIds = (): number[] =>
  JSON.parse(
    localStorage.getItem(LocalStorageKeys.LAST_ACCOUNT_STRATEGY_IDS) || "[]",
  );
export const getLastPerformanceStrategyPeriods = (): ColumnPeriodT[] =>
  JSON.parse(
    localStorage.getItem(LocalStorageKeys.LAST_PERIODS_STRATEGY) || "[]",
  );

export const getLastReportTab = (): ReportTabT =>
  localStorage.getItem(LocalStorageKeys.LAST_REPORT_TAB) === "strategy"
    ? "strategy"
    : "stock";

export const adjustFactsetOptionSymbol = (opt: FactSetOption) => {
  const { REQ_SYM, ...rest } = opt;
  const reqSym = REQ_SYM.replace("-USA", "");
  const symbolParts = reqSym.split("#");
  return { ...rest, REQ_SYM: symbolParts[0].padEnd(6, " ") + symbolParts[1] };
};

export const generateStrikeIntervals = (maxValue: number, step: number = 5) => {
  const intervals = [];

  for (let i = step; i <= maxValue; i += step) {
    intervals.push(i);
  }

  return intervals;
};

export const getStrategyPickerHighlightColor = (strategy: Strategy) => {
  const { current_call__symbol: symbol } = strategy;
  if (symbol === null) {
    return "rgba(0,255,0,0.3)";
  }

  const expDate = dayjs(symbol.slice(6, 12), "YYMMDD");
  const daysDiff = dayjs(expDate).diff(dayjs(), "day");
  if (daysDiff === 0) {
    return "rgba(255,0,0,0.3)";
  } else if (daysDiff < 7) {
    return "rgba(255,255,0,0.3)";
  }

  return "#fff";
};

export const getPositionHighlightColor = (pos: StrategyPosition) => {
  const { overriden_option_qty } = pos;
  if (overriden_option_qty !== null) {
    return "rgba(255,255,0,0.8)";
  }

  return "inherit";
};

export const getOptionQty = (pos: StrategyPosition) => {
  const { overriden_option_qty, option_qty } = pos;
  if (overriden_option_qty !== null || option_qty !== null) {
    return parseInt(overriden_option_qty || option_qty);
  }

  return null;
};

export const formatFactsetNumberValue = (value: string) => {
  if (!value || value.length === 0 || value === "N/A") {
    return "N/A";
  }

  const numValue = parseFloat(value);
  return numValue.toFixed(2);
};

export interface OptionSymbolParams {
  stockSymbol?: string;
  expirationDate?: dayjs.Dayjs;
  type?: string;
  strikePrice?: number;
  isCorrect: boolean;
}

export const parseOptionSymbol = (symbol: string): OptionSymbolParams => {
  const symbolParams: OptionSymbolParams = { isCorrect: true };
  if (symbol.length !== 21) {
    symbolParams.isCorrect = false;
    return symbolParams;
  }

  symbolParams.stockSymbol = symbol.slice(0, 6).trim();
  symbolParams.expirationDate = dayjs(symbol.slice(6, 12), "YYMMDD");
  symbolParams.type = symbol.slice(12, 13);
  symbolParams.strikePrice = parseInt(symbol.slice(13)) / 1_000;

  return symbolParams;
};

export const mapOptionNameFromParams = (params: OptionSymbolParams) => {
  if (params.isCorrect) {
    const { stockSymbol, expirationDate, strikePrice, type } = params;
    if (stockSymbol && expirationDate && strikePrice && type) {
      return mapOptionName(
        stockSymbol,
        expirationDate,
        String(strikePrice),
        type,
      );
    }

    return "Missing option symbols params";
  }

  return "Incorrect option symbol";
};

export const getStrategyOptionQty = (strategy: Strategy) => {
  const optionQty = parseInt(strategy.option_qty || "0");
  const executedOpenQty = parseInt(strategy.executed_open_qty || "0");
  const executedCloseQty = parseInt(strategy.executed_close_qty || "0");
  return optionQty + executedOpenQty + executedCloseQty;
};

export const getDaysToExDividend = (strategy: Strategy) => {
  const exDate = strategy.ex_date_next || strategy.ex_date_last;
  if (!exDate) {
    return null;
  }

  const today = dayjs().startOf("day");
  const exDateDayjs = dayjs(exDate);

  const daysToExDividend = exDateDayjs.diff(today, "days");
  return daysToExDividend;
};

export const getDaysToEarningsDate = (strategy: Strategy) => {
  const earningsDate = strategy.next_earnings_date;
  if (!earningsDate) {
    return null;
  }

  const today = dayjs().startOf("day");
  const earnigsDateDayjs = dayjs(earningsDate);

  const daysToEarngingsDate = earnigsDateDayjs.diff(today, "days");
  return daysToEarngingsDate;
};

export const getHomepageDateColumnHighlightColor = (daysTo: number | null) => {
  if (daysTo === null || daysTo > 7) {
    return "inherit";
  } else if (daysTo <= 7 && daysTo > 1) {
    return "rgba(255,255,0,0.5)";
  } else if (daysTo === 0 || daysTo === 1) {
    return "rgb(0,255,0,0.5)";
  } else if (daysTo < 0) {
    return "rgba(128, 128, 128, 0.5)";
  }

  return "inherit";
};

export const formatPercent = (value: number) =>
  Number(value).toLocaleString(undefined, {
    style: "percent",
    minimumFractionDigits: 2,
  });

export const formatUsDate = (value: string) =>
  dayjs(value, "YYYY-MM-DD").format("MM/DD/YYYY");

export const formatReportNumber = (value: number) => value.toFixed(2);

export const formatAdjustmentType = (adjustmentType: string): string =>
  adjustmentType.replace(/_/g, " ");

export const optionSymbolToName = (symbol?: string | null) => {
  if (!symbol) {
    return "N/A";
  }

  const symbolParams = parseOptionSymbol(symbol);

  return mapOptionNameFromParams(symbolParams);
};

export const getPreviousWeekday = (endDate: string): Dayjs => {
  let date = dayjs(endDate);

  // Check if the date is a weekday (Monday to Friday)
  while (
    date.day() === 0 ||
    date.day() === 6 ||
    date.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD")
  ) {
    // 0 = Sunday, 6 = Saturday
    date = date.subtract(1, "day"); // Subtract one day
  }

  return date;
};

export const getNextWeekday = (startDate: string): Dayjs => {
  let date = dayjs(startDate);

  // Check if the date is a weekday (Monday to Friday)
  while (
    date.day() === 0 ||
    date.day() === 6 ||
    date.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD")
  ) {
    // 0 = Sunday, 6 = Saturday
    date = date.add(1, "day"); // Add one day
  }

  return date;
};

export const getDefaultColumnPeriods = (): ColumnPeriodT[] => {
  const DEFAULT_PERIODS: ColumnPeriodT[] = [
    {
      name: "YTD",
      period: ["Select date range", "Select date range"] as [string, string],
    },
    {
      name: "1 YR",
      period: ["Select date range", "Select date range"] as [string, string],
    },
    {
      name: "3 YR",
      period: ["Select date range", "Select date range"] as [string, string],
    },
    {
      name: "Period Performance",
      period: ["Select date range", "Select date range"] as [string, string],
    },
  ];

  const lastRange = getLastStatisticDateRange()
    ? getLastStatisticDateRange()
    : null;
  if (!lastRange) {
    return DEFAULT_PERIODS;
  }

  const startDate = getNextWeekday(lastRange[0] || "");
  const endDate = getPreviousWeekday(lastRange[1] || "");
  const prevWeekdayStr = endDate.format("YYYY-MM-DD");

  const calculateDate = (date: Dayjs) =>
    getPreviousWeekday(date.format("YYYY-MM-DD")).format("YYYY-MM-DD");

  const jan1 = calculateDate(dayjs(endDate).startOf("year"));
  const oneYearAgo = calculateDate(dayjs(endDate).subtract(1, "year"));
  const threeYearsAgo = calculateDate(dayjs(endDate).subtract(3, "year"));
  const periodPerformance: [string, string] = [
    startDate.format("YYYY-MM-DD"),
    endDate.format("YYYY-MM-DD"),
  ];

  return [
    { name: "YTD", period: [jan1, prevWeekdayStr] as [string, string] },
    { name: "1 YR", period: [oneYearAgo, prevWeekdayStr] as [string, string] },
    {
      name: "3 YR",
      period: [threeYearsAgo, prevWeekdayStr] as [string, string],
    },
    { name: "Period Performance", period: periodPerformance },
  ];
};
